import { Link } from '@tanstack/react-router';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { HelpArticle } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { articleState } from '../schemas/article-state';

import { ArticleStateBadge } from './article-state-badge';
import { InternalBadge } from './internal-badge';

/** Column model of the articles table; `meta` ties columns to the list's sorts and filters. */
export function useHelpArticleColumns(storeId: string) {
  const { t } = useTranslation('helpCenter');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<HelpArticle>>>(
    () => [
      {
        id: 'title',
        header: t('columns.article'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <Link
              to="/s/$storeId/help/$articleId"
              params={{ storeId, articleId: row.original.id }}
              className="block truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.title}
            </Link>
            <code className="text-muted-foreground block truncate text-xs">
              {row.original.handle}
            </code>
          </div>
        ),
        meta: { label: t('columns.article'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'category',
        header: t('columns.category'),
        cell: ({ row }) =>
          row.original.category ? (
            <span className="flex items-center gap-1.5 text-sm">
              <span className="truncate">{row.original.category.name}</span>
              {row.original.category.visibility === 'internal' ? <InternalBadge /> : null}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">{t('none')}</span>
          ),
        meta: { label: t('columns.category'), filterKey: 'categoryId' },
      },
      {
        id: 'state',
        header: t('columns.state'),
        cell: ({ row }) => <ArticleStateBadge state={articleState(row.original)} />,
        meta: { label: t('columns.state'), filterKey: 'status' },
      },
      {
        id: 'helpfulness',
        header: t('columns.helpfulness'),
        cell: ({ row }) => (
          <span
            className="flex items-center gap-3 text-sm tabular-nums"
            aria-label={t('helpfulness.label', {
              yes: row.original.helpfulYes,
              no: row.original.helpfulNo,
            })}
          >
            <span className="flex items-center gap-1">
              <ThumbsUp className="text-muted-foreground size-3.5" aria-hidden />
              {format.number(row.original.helpfulYes)}
            </span>
            <span className="flex items-center gap-1">
              <ThumbsDown className="text-muted-foreground size-3.5" aria-hidden />
              {format.number(row.original.helpfulNo)}
            </span>
          </span>
        ),
        meta: { label: t('columns.helpfulness') },
      },
      {
        id: 'views',
        header: t('columns.views'),
        cell: ({ row }) => (
          <span className="tabular-nums">{format.number(row.original.views)}</span>
        ),
        meta: { label: t('columns.views'), sortField: 'views', align: 'end' },
      },
      {
        id: 'position',
        header: t('columns.position'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.position}</span>,
        meta: { label: t('columns.position'), sortField: 'position', align: 'end' },
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span
            className="text-muted-foreground whitespace-nowrap"
            title={format.utc(row.original.updatedAt)}
          >
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { label: t('columns.updated'), sortField: 'updatedAt' },
      },
    ],
    [format, storeId, t],
  );
}
